import { FC, FormEvent, useEffect } from 'react';
import { Button, Box, Typography, Modal, Paper, FormLabel } from '@mui/material';

import { COMMITMENT_TYPE_OPTIONS, COMPENSATION_TYPE_OPTIONS } from '@libs/types';
import { JobCreateDto } from '@libs/dto';
import {
  TextInput,
  Select,
  RooftopSelect,
  CheckBoxInput,
  FreeSoloInput,
  RichTextEditor,
  CheckboxListInput,
  JobPreview,
} from '@carcareers/components';
import { useForm } from '@carcareers/contexts';
import { useDrawer } from '@carcareers/contexts';
import { KEYWORDS, BENEFITS } from '@carcareers/utils';
import { useJob } from '@carcareers/hooks';
import { useState } from 'react';

interface JobCreateFormProps {
  onSuccess?: () => void;
  isStep?: boolean;
  rooftopName?: string;
}

export const JobCreateForm: FC<JobCreateFormProps> = ({ onSuccess, isStep, rooftopName = 'rooftop' }) => {
  const { setOpen } = useDrawer();
  const { data, errors, setData, setErrors, getErrors } = useForm<JobCreateDto>();
  const { createJob } = useJob();
  const [preview, setPreview] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const response = await createJob(data, { onSuccess });

    if (response?.data) {
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  useEffect(() => {
    setDescriptionError(getErrors('description'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <>
      <Typography variant="h6">Job Details</Typography>
      <TextInput label="Title" name="title" />
      <RooftopSelect name={rooftopName} />
      <Select fullWidth label="Commitment Type" name="commitmentType" options={COMMITMENT_TYPE_OPTIONS} />
      <Typography variant="h6">Compensation</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextInput fullWidth label="($) Min" name="compensationMin" type="number" />
        <TextInput fullWidth label="($) Max" name="compensationMax" type="number" />
        <Select fullWidth label="Per" name="compensationType" options={COMPENSATION_TYPE_OPTIONS} />
      </Box>
      <CheckBoxInput label="Commission" name="hasCommission" />
      <Typography variant="h6">Keywords</Typography>
      <FreeSoloInput
        multiple
        disableCloseOnSelect
        label="Keywords"
        name="keywords"
        suggestions={KEYWORDS.map((keyword) => ({ label: keyword, value: keyword }))}
        value={data?.keywords}
      />
      <Typography variant="h6">Benefits</Typography>
      <CheckboxListInput
        name="benefits"
        variant="chip"
        options={BENEFITS.map((benefit) => ({ label: benefit.name, value: benefit.value }))}
      />
      <Typography variant="h6">Description</Typography>
      {descriptionError && <FormLabel error={!!descriptionError}>{descriptionError}</FormLabel>}
      <RichTextEditor data={data?.description} onUpdate={(value) => setData('description', value)} />
      <Modal open={preview} onClose={() => setPreview(false)}>
        <Paper
          sx={{
            overflowY: 'scroll',
            position: 'absolute',
            top: 100,
            left: 100,
            bottom: 100,
            right: 100,
            p: { xs: 2, sm: 2, md: 4 },
          }}
        >
          <JobPreview data={data} />
        </Paper>
      </Modal>
      {!isStep && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            gap: theme.spacing(2),
            justifyContent: 'flex-end',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
            [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
          })}
        >
          <Button size="large" variant="outlined" color="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={() => setPreview(true)}>
            Preview
          </Button>
          <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
            Create Job
          </Button>
        </Box>
      )}
    </>
  );
};

export default JobCreateForm;
